/**
 * A module-level registry describing what the CURRENT page lets the assistant
 * do, and what it can see.
 *
 * Module-level and not React context ON PURPOSE: `AssistantProvider` is mounted
 * at the top of `AppRoutes`, ABOVE every route, and React context flows down —
 * so a provider mounted inside `/map` is structurally invisible to it. This
 * mirrors `useUnsavedWork`, `robot-positions-store` and
 * `insufficient-credits-emitter`, which solve the same out-of-tree problem the
 * same way.
 *
 * This registry is also the SINGLE source of `surface.availableActions`. On a
 * page that registers nothing the array is empty, and the server-side narrowing
 * withholds every map tool — which is why the assistant cannot claim to move a
 * map on `/settings`.
 *
 * At most ONE surface registers at a time in practice (one route is mounted,
 * and the kinds are `map`, `dashboard` and `library`), but the registry does not
 * enforce that — the LAST registration wins, and unregistering clears only
 * itself. Same deliberate limit `useUnsavedWork` states for the same reason. A
 * second surface kind (say a dashboard that also holds a map) would therefore
 * OVERWRITE rather than compose; composing them is a real design decision, not
 * a line of code, so it should be made rather than stumbled into.
 */

import type { DashboardBridge, LayerSampleSnapshot } from '@/components/organisms/Dashboard/DashboardBridge'
import type { FormOp } from '@/pages/forms/lib/apply-form-ops'
import type { NotebookBridgeHandle } from '@/pages/scripts/hooks/notebook-bridge-client'
import type {
  AssistantCapacityContext,
  AssistantFormsContext,
  AssistantGeocodingContext,
  AssistantLibraryContext,
  AssistantMonitoringContext,
  AssistantScriptsContext,
  AssistantSecurityContext,
  AssistantSettingsContext,
} from '@/services/api/domains/chatbot.api'
import type { DashboardElementType } from '@/services/api/domains/dashboards.api'

import type { ConfirmFn } from './confirmClientTool'
import type {
  LayerOperation,
  ListBasemapsResult,
  MapSettingsRequest,
  MapSettingsResult,
  SetBasemapResult,
  StyleLayerRequest,
  StyleLayerResult,
  UpdateMapLayersResult,
} from './map-tool-types'

import type { MapEngineType } from '@/components/organisms/Map/types/engine.types'

export type * from './map-tool-types'

/** One layer's outcome from an add. Reports what HAPPENED, never what was asked. */
export type AddLayerOutcome =
  | { kind: 'added'; layerId: string; name: string }
  | { kind: 'already_present'; layerId: string; name: string }
  | { kind: 'not_found'; layerId: string }
  | { kind: 'failed'; layerId: string; reason: string }

export type ToggleOutcome =
  | { kind: 'visible'; visible: boolean }
  | { kind: 'not_on_map' }
  | { kind: 'failed'; reason: string }
export type RemoveOutcome = { kind: 'removed' } | { kind: 'not_on_map' } | { kind: 'failed'; reason: string }
export type ZoomOutcome =
  | { kind: 'zoomed'; extent: [number, number, number, number] }
  | { kind: 'no_extent' }
  | { kind: 'not_on_map' }
  | { kind: 'failed'; reason: string }

/**
 * Outcome of a coordinate move. Reports the center and zoom the map ACTUALLY
 * applied, so "I moved the view" is a statement about the map, not about the
 * request — the whole reason `zoom_to_coordinates` exists rather than the model
 * narrating a layer-extent zoom as a place zoom.
 */
export type ZoomToCoordinatesOutcome =
  | { kind: 'moved'; center: [number, number]; zoom: number }
  | { kind: 'failed'; reason: string }

/** What the assistant can SEE of the map, read synchronously at send time. */
export interface MapSurfaceContext {
  mapState: {
    center: [number, number]
    zoom: number
    /**
     * Narrowed to the wire's own enum, NOT `string`. The chat request schema is
     * `z.enum(['openlayers','arcgis'])`, so a widened type here would only be
     * re-narrowed by a cast at the send site — turning what the compiler could
     * have caught into a 400 that fails the whole turn at runtime.
     */
    engineType: MapEngineType
    visibleLayerIds: string[]
    /** 2D or 3D, from the page's own state. */
    viewType?: '2d' | '3d'
    /** Layer groups, flattened from the tree. Present only when the map has a group. */
    groups?: Array<{ id: string; title: string; childLayerIds: string[] }>
    /** The open map's identity and unsaved changes; read at send time. */
    map?: { id: string | null; name: string | null; canSave: boolean; unsavedChanges: string[]; isNew: boolean }
    basemap?: { id: string | null; name: string | null }
    /** Names of the enabled ArcGIS widgets. Omitted on OpenLayers. */
    widgets?: string[]
  }
  layerSamples: Array<{
    layerId: string
    layerName: string
    /**
     * Narrowed to the wire's own enum, NOT `string` — same rationale as
     * `engineType` above. The internal `LayerType` enum does not match this
     * one 1:1 (`postgres`/`csv` are spelled differently on the wire, `xyz` has
     * no direct equivalent), so a widened type here would let a caller send an
     * internal value straight through and fail the whole turn with a 400.
     * Producers must go through `toBridgeLayerType` (`Map/lib/layer-type-mapping`).
     */
    layerType: LayerSampleSnapshot['layerType']
    visible?: boolean
    /** 0-1. */
    opacity?: number
    /** One-line style summary (see `summarizeStyle`). */
    style?: string
  }>
}

export interface SaveMapRequest {
  mode: 'save' | 'save_as'
  name?: string
}

export type SaveMapResult =
  | { kind: 'saved'; mode: 'save' | 'save_as' | 'create'; mapId: string; name: string; changes: string[] }
  | { kind: 'declined'; reason: string }
  | {
      kind: 'refused'
      reason:
        | 'not_allowed'
        | 'name_required'
        | 'nothing_to_save'
        | 'confirmation_unavailable'
        | 'map_changed'
        | 'changed_while_asking'
    }
  | { kind: 'failed'; reason: string }

export interface MapSurfaceHandle {
  kind: 'map'
  actions: {
    saveMap(request: SaveMapRequest, confirm: ConfirmFn | undefined): Promise<SaveMapResult>
    addLayer(layerIds: string[]): Promise<AddLayerOutcome[]>
    toggleLayer(layerId: string, visible: boolean): Promise<ToggleOutcome>
    removeLayer(layerId: string): Promise<RemoveOutcome>
    zoomToLayer(layerId: string): Promise<ZoomOutcome>
    zoomToCoordinates(center: [number, number], zoom?: number): Promise<ZoomToCoordinatesOutcome>
    updateMapLayers(operations: LayerOperation[]): Promise<UpdateMapLayersResult>
    listBasemaps(): Promise<ListBasemapsResult>
    setBasemap(basemapId: string): Promise<SetBasemapResult>
    setMapSettings(
      request: MapSettingsRequest,
    ): Promise<MapSettingsResult | { kind: 'failed'; reason: 'engine_not_ready' }>
    styleLayer(request: StyleLayerRequest): Promise<StyleLayerResult>
  }
  /** Null when the map is not ready yet — callers must treat that as "no context". */
  readContext(): MapSurfaceContext | null
}

/**
 * A mounted dashboard (`DashboardView` or `DashboardBuilder`).
 *
 * Unlike the map surface this carries NO `actions`: the four dashboard tools are
 * SERVER tools that read through `ctx.dashboardsClient`, and their browser half
 * is `dispatchToolToBridge` — not a client executor. What the browser must
 * supply is therefore only three things: which dashboard is on screen (for the
 * server reads), the bridge (for the effects), and what is mounted (for the
 * gate).
 */
export interface DashboardSurfaceHandle {
  kind: 'dashboard'
  /** The dashboard the user is looking at. Rides the request so the server can read it AS THIS CALLER. */
  dashboardId: string
  bridge: DashboardBridge
  /**
   * A GETTER, not an array — the same shape decision, for the same reason, as
   * `RobotRegistryContextValue.registerRobotContext`. Elements come and go in
   * the builder; a snapshot taken at registration would be wrong by the next
   * turn, and re-registering on every change would mean registering on every
   * render.
   */
  elementTypes(): DashboardElementType[]
}

/** The Library list or an item page: what the assistant can SEE there, and (list page only) the import dialog. */
export interface LibrarySurfaceHandle {
  kind: 'library'
  readContext(): { library: AssistantLibraryContext }
  /** Opens the Import dialog on that source. Only the list page provides it, and only for a writer. */
  openImport?(source: 'file' | 'server'): void
}

/** A /geocoding page (wizard, sessions list or one run's results): what the assistant can SEE there. */
export interface GeocodingSurfaceHandle {
  kind: 'geocoding'
  readContext(): { geocoding: AssistantGeocodingContext }
}

/** What applying an `edit_form` batch to the open builder did. All or nothing: on a refusal nothing changed. */
export type FormEditResult =
  | { ok: true; changed: string[] }
  | {
      ok: false
      error: 'not_open' | 'not_found' | 'locked' | 'invalid'
      issues?: { field: string; message: string }[]
    }

/** A /forms page (list, builder or submissions): what the assistant can SEE there, and (builder only) edit. */
export interface FormsSurfaceHandle {
  kind: 'forms'
  readContext(): { forms: AssistantFormsContext }
  /** Builder only: apply an edit_form batch to the open form. Never saves; the user saves with Save. */
  applyEdit?(formId: string, ops: FormOp[]): FormEditResult
}

/** A /scripts page (list or one script): what the assistant can SEE there, and (live session only) the notebook bridge. */
export interface ScriptsSurfaceHandle {
  kind: 'scripts'
  readContext(): { scripts: AssistantScriptsContext }
  /**
   * The JupyterLab bridge of the notebook open in a ready session on this page; absent otherwise. A getter,
   * read at CALL time: the session opens and the bridge says ready long after the page registered.
   */
  readonly bridge?: NotebookBridgeHandle
}

/** A /security page (overview, scan type, one target, agents): what the assistant can SEE there. */
export interface SecuritySurfaceHandle {
  kind: 'security'
  readContext(): { security: AssistantSecurityContext }
}

/** A /monitoring page (site library or one site): what the assistant can SEE there. */
export interface MonitoringSurfaceHandle {
  kind: 'monitoring'
  readContext(): { monitoring: AssistantMonitoringContext }
}

/** A /capacity page (run list or one run): what the assistant can SEE there. */
export interface CapacitySurfaceHandle {
  kind: 'capacity'
  readContext(): { capacity: AssistantCapacityContext }
}

/** Settings → Users or Groups: what the assistant can SEE there. */
export interface SettingsSurfaceHandle {
  kind: 'settings'
  readContext(): { settings: AssistantSettingsContext }
  /** Settings → Users only: opens the Add User form pre-filled. There is no password here, ever. */
  openAddUser?(initial: AddUserPrefill): void
  /** Settings → Users only: seats still free for a role (max - used - tenant allocations), or null before the quotas load. */
  freeSeats?(role: 'admin' | 'publisher' | 'viewer'): number | null
}

/** What the assistant may pre-fill in the Add User form. The admin types the password. */
export interface AddUserPrefill {
  name?: string
  email?: string
  role?: 'admin' | 'publisher' | 'viewer'
  isGuest?: boolean
}

export type AssistantSurfaceHandle =
  | MapSurfaceHandle
  | DashboardSurfaceHandle
  | LibrarySurfaceHandle
  | GeocodingSurfaceHandle
  | FormsSurfaceHandle
  | ScriptsSurfaceHandle
  | SecuritySurfaceHandle
  | MonitoringSurfaceHandle
  | CapacitySurfaceHandle
  | SettingsSurfaceHandle

/**
 * Client actions that EVERY registered surface can perform, whatever it is.
 *
 * `availableActions` is a NARROWING CLAIM: the moment a surface registers, the
 * server filters the client-tool pool down to exactly this list, so anything
 * omitted is withheld for that turn. `navigate_to` is route-based — it needs no
 * engine, no bridge and no page-scoped state — so omitting it silently removed
 * the assistant's ability to navigate AWAY from `/map`, and withheld it
 * entirely while a registered map was still initialising.
 *
 * The symptom was not an error: with the tool gone its prompt guidance is
 * dropped too (`subtractDisabledTools`), so the model improvised an
 * explanation — "I can only help with map-related actions" — for a capability
 * it could no longer see. Every branch of `buildSitewideSurface` that sends an
 * explicit list must include these.
 *
 * `request_user_confirmation` joins it for the same structural reason, with a
 * sharper consequence. The assistant panel is mounted on every authenticated
 * route, so the claim is TRUE everywhere — but a client tool missing from this
 * list is stripped on every page that registers a surface, `/map` included, and
 * `ctx.confirmationAvailable` then goes false. `delete_feature` would refuse
 * outright on exactly the page an edit is most likely to start from, while
 * still working from `/library`, which registers nothing and therefore narrows
 * nothing. A page that claims nothing at all (`availableActions` absent) is
 * unaffected either way: `applyAvailableActions` narrows nothing on an absent
 * claim.
 *
 * `show_library` and `open_import_dialog` navigate to `/library` (`show_geocoding` to a
 * `/geocoding` page, `show_form` to a `/forms` page, `show_notebook` to a `/scripts` page, `show_security` to a `/security` page, `show_monitoring` to a `/monitoring` or `/capacity` page, `open_add_user` and `show_settings` to Settings → Users or Groups) by themselves, so they are claimable everywhere: a continuation keeps the
 * claims of the page that started the turn, and a claim made only on `/library` would strand a
 * user asking from `/map`.
 */
export const ALWAYS_AVAILABLE_SURFACE_ACTIONS = [
  'navigate_to',
  'request_user_confirmation',
  'show_library',
  'open_import_dialog',
  'show_geocoding',
  'show_form',
  'show_notebook',
  'show_security',
  'show_monitoring',
  'open_add_user',
  'show_settings',
] as const

/** The client tools this surface kind makes available, in wire order. */
export const MAP_SURFACE_ACTIONS = [
  'add_layer_to_map',
  'toggle_layer_visibility',
  'remove_layer_from_map',
  'zoom_to_layer',
  'zoom_to_coordinates',
  'save_map',
  'update_map_layers',
  'list_basemaps',
  'set_basemap',
  'set_map_settings',
  'style_layer',
] as const

/** The tools a mounted dashboard makes available, whatever it contains. */
export const DASHBOARD_SURFACE_ACTIONS = [
  'list_dashboard_elements',
  'get_dashboard_data',
  'compare_indicators',
  'refresh_element',
  // Authoring (Phase 4). A name in the server's BRIDGE_DEPENDENT_SITEWIDE_TOOLS
  // but NOT here is a DEAD TOOL: the server narrows it away on every turn
  // because the browser never claims it. That is how toggle_layer/zoom_to_feature
  // were caught in the Phase 3 review, and it is why the parity test now asserts
  // BOTH directions rather than only that every claim is bridge-dependent.
  'add_dashboard_element',
  // Task 4: `configure_dashboard_element` — same posture as `add_dashboard_element` above.
  'configure_dashboard_element',
] as const

/**
 * The tools that additionally require a `map` ELEMENT on the dashboard.
 *
 * Only these two — not the full four map-affecting tools that exist. Both
 * `zoom_to_feature` and `toggle_layer` resolve a layer EXCLUSIVELY via
 * `resolveLayer(name, ctx.requestContext.layerSamples ?? [], ...)`, and a
 * dashboard turn never sends `layerSamples` (that shape describes the `/map`
 * engine's own context; sending it here would feed `resolveLayer` names it
 * cannot resolve). Offering either on a dashboard surface therefore buys a
 * guaranteed `not_found` — the model would appear to have layer-toggle and
 * feature-zoom capability it structurally cannot exercise. Giving a
 * dashboard map real layer awareness (so these two could work) is future
 * work, not a line of code, so they stay off this list until that lands.
 *
 * (There is no `highlight_features` tool — highlighting is a bridge effect
 * of `zoom_to_feature` and the query tools.)
 */
/** The client tool the Forms builder adds when it can apply edits (`FormsSurfaceHandle.applyEdit`). */
export const FORMS_BUILDER_SURFACE_ACTIONS = ['edit_form'] as const

/**
 * The client tools a script page adds while its live notebook's bridge is ready (`ScriptsSurfaceHandle.bridge`).
 * Claimed only then, so every other page (and a session whose notebook tab has not connected) narrows them away;
 * the server also refuses them `not_open` unless the page context says the same.
 */
export const SCRIPTS_SESSION_SURFACE_ACTIONS = ['read_open_notebook', 'edit_notebook', 'run_cells'] as const

export const MAP_ELEMENT_SURFACE_ACTIONS = ['zoom_to_location', 'clear_map_overlays'] as const

let current: AssistantSurfaceHandle | null = null

export function getAssistantSurface(): AssistantSurfaceHandle | null {
  return current
}

/**
 * Register `handle` as the active surface. Returns an unregister function that
 * MUST run on unmount.
 */
export function registerAssistantSurface(handle: AssistantSurfaceHandle): () => void {
  current = handle

  return () => {
    // Identity guard, exactly as in `useUnsavedWork`: a page that unmounts
    // AFTER its successor already registered must clear only itself. An
    // unconditional `current = null` would leave the assistant blind on a page
    // that is really showing a map.
    if (current === handle) current = null
  }
}

/**
 * The registered surface, but only if it is a dashboard.
 *
 * Presence alone is never enough — the same reason `buildSitewideSurface`
 * checks `kind === 'map'` before advertising map actions. A caller that wants a
 * dashboard must not be handed a map handle that happens to be registered.
 */
export function getDashboardSurface(): DashboardSurfaceHandle | null {
  return current && current.kind === 'dashboard' ? current : null
}

/** Test-only reset of the module state. */
export function __resetAssistantSurfaceForTests(): void {
  current = null
}
